import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Clock, Home, BarChart2 } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { api } from '../services/api';
import { ElectionSummary } from '../types';
import { setSiteMeta } from '../utils/meta';

export const VotingStatusErrorPage: React.FC = () => {
  const location = useLocation();
  const [election, setElection] = useState<ElectionSummary>(() => api.getElectionSettingsSync());

  const pathname = location.pathname.toLowerCase();
  const isNotStarted = pathname.includes('votingnotstarted');
  const isEnded = pathname.includes('votingended');
  const isAlreadyVoted = pathname.includes('youhavevoted');
  const isNotLogged = pathname.includes('notlogged');

  useEffect(() => {
    api.getElectionInfo().then((info) => {
      if (info) setElection(info);
    });
  }, []);

  let title = '401 Error: Voting has not started!';
  let badgeColor = '#FF9900';
  let badgeBg = 'rgba(255, 153, 0, 0.1)';
  let badgeBorder = 'rgba(255, 153, 0, 0.3)';
  let badgeText = 'Polls Not Open Yet';
  let illustrationSrc = '/assets/images/401 Error Unauthorized-pana.svg';

  if (isEnded) {
    title = '401 Error: Voting has ended!';
    badgeColor = '#EF4444';
    badgeBg = 'rgba(239, 68, 68, 0.1)';
    badgeBorder = 'rgba(239, 68, 68, 0.3)';
    badgeText = 'Polls Officially Closed';
  } else if (isAlreadyVoted) {
    title = '401 Error: It seems you have voted already';
    badgeColor = '#10B981';
    badgeBg = 'rgba(16, 185, 129, 0.1)';
    badgeBorder = 'rgba(16, 185, 129, 0.3)';
    badgeText = 'Ballot Already Cast';
  } else if (isNotLogged) {
    title = "404 Error: You aren't logged in";
    badgeColor = '#6B7280';
    badgeBg = 'rgba(107, 114, 128, 0.1)';
    badgeBorder = 'rgba(107, 114, 128, 0.3)';
    badgeText = 'Authentication Required';
    illustrationSrc = '/assets/images/404 error with portals-pana.svg';
  }

  useEffect(() => {
    let pageTitle = 'Notice';
    if (isNotStarted) pageTitle = 'Voting Not Started';
    else if (isEnded) pageTitle = 'Voting Ended';
    else if (isAlreadyVoted) pageTitle = 'Already Voted';
    else if (isNotLogged) pageTitle = 'Authentication Required';
    setSiteMeta({ election, pageTitle });
  }, [isNotStarted, isEnded, isAlreadyVoted, isNotLogged, election]);

  const formatDateTime = (iso?: string) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return null;
      return d.toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return null;
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Official Electpoll Brand Navigation */}
      <Header />

      {/* Main Error Page Container (matches legacy .error-page / .error) */}
      <main
        style={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '48px 24px',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            maxWidth: '680px',
            width: '100%',
            margin: '0 auto',
          }}
        >
          {/* Legacy Illustration */}
          <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
            <img
              src={illustrationSrc}
              alt="Electpoll Notice"
              style={{
                maxWidth: '380px',
                width: '100%',
                height: 'auto',
                maxHeight: '280px',
                objectFit: 'contain',
                display: 'block',
              }}
              onError={(e) => {
                (e.target as HTMLElement).style.display = 'none';
              }}
            />
          </div>

          {/* Status Badge */}
          <div style={{ marginBottom: '14px', display: 'flex', justifyContent: 'center' }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '5px 14px',
                borderRadius: '999px',
                backgroundColor: badgeBg,
                border: `1px solid ${badgeBorder}`,
                color: badgeColor,
                fontSize: '11.5px',
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                fontFamily: "'Syne', sans-serif",
              }}
            >
              {badgeText}
            </span>
          </div>

          {/* Heading & Legacy Text */}
          <div className="ep-texts">
            <h2
              style={{
                fontFamily: "'Syne', sans-serif",
                fontSize: '26px',
                fontWeight: 700,
                color: '#171619',
                margin: '0 0 12px 0',
                lineHeight: 1.3,
              }}
            >
              {title}
            </h2>

            {/* Paragraph matching authentic legacy texts */}
            {isNotStarted && (
              <p
                style={{
                  fontSize: '15.5px',
                  color: '#4B5563',
                  lineHeight: 1.65,
                  margin: '0 auto 28px auto',
                  maxWidth: '600px',
                  fontFamily: "'Noto Sans', -apple-system, BlinkMacSystemFont, sans-serif",
                }}
              >
                The set voting time has not reached; voting has not begun therefore you have been logged out and cannot cast a vote yet. If you think this is a mistake, return to the homepage{' '}
                <Link
                  to="/"
                  style={{
                    color: '#FF9900',
                    textDecoration: 'underline',
                    fontWeight: 600,
                  }}
                >
                  here
                </Link>{' '}
                and report a problem.
              </p>
            )}

            {isEnded && (
              <p
                style={{
                  fontSize: '15.5px',
                  color: '#4B5563',
                  lineHeight: 1.65,
                  margin: '0 auto 28px auto',
                  maxWidth: '600px',
                  fontFamily: "'Noto Sans', -apple-system, BlinkMacSystemFont, sans-serif",
                }}
              >
                The voting deadline has been reached and therefore you can no longer cast a vote. If you think this is a mistake, return to the homepage{' '}
                <Link
                  to="/"
                  style={{
                    color: '#FF9900',
                    textDecoration: 'underline',
                    fontWeight: 600,
                  }}
                >
                  here
                </Link>{' '}
                and report a problem.
              </p>
            )}

            {isAlreadyVoted && (
              <p
                style={{
                  fontSize: '15.5px',
                  color: '#4B5563',
                  lineHeight: 1.65,
                  margin: '0 auto 28px auto',
                  maxWidth: '600px',
                  fontFamily: "'Noto Sans', -apple-system, BlinkMacSystemFont, sans-serif",
                }}
              >
                Our system says you have voted already therefore you are not authorized to proceed. If you think this is a mistake, return to the homepage{' '}
                <Link
                  to="/"
                  style={{
                    color: '#FF9900',
                    textDecoration: 'underline',
                    fontWeight: 600,
                  }}
                >
                  here
                </Link>{' '}
                and report a problem.
              </p>
            )}

            {isNotLogged && (
              <p
                style={{
                  fontSize: '15.5px',
                  color: '#4B5563',
                  lineHeight: 1.65,
                  margin: '0 auto 28px auto',
                  maxWidth: '600px',
                  fontFamily: "'Noto Sans', -apple-system, BlinkMacSystemFont, sans-serif",
                }}
              >
                You might want to check again but you seem to not be logged in and therefore can't access this page. You can return to the homepage{' '}
                <Link
                  to="/"
                  style={{
                    color: '#FF9900',
                    textDecoration: 'underline',
                    fontWeight: 600,
                  }}
                >
                  here
                </Link>{' '}
                and login to gain access.
              </p>
            )}
          </div>

          {/* Official Election Schedule Card (Dark elevation for rich brand contrast) */}
          {(isNotStarted || isEnded || election?.startTime || election?.endTime) && (
            <div
              style={{
                maxWidth: '520px',
                margin: '0 auto 28px auto',
                backgroundColor: '#171619',
                border: '1px solid #2D2C31',
                borderRadius: '14px',
                padding: '18px 22px',
                textAlign: 'left',
                boxShadow: '0 12px 28px -6px rgba(23, 22, 25, 0.12)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '12px',
                  borderBottom: '1px solid #2D2C31',
                  paddingBottom: '10px',
                }}
              >
                <div
                  style={{
                    fontFamily: "'Syne', sans-serif",
                    fontWeight: 700,
                    fontSize: '13.5px',
                    color: '#FF9900',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '7px',
                  }}
                >
                  <Clock size={15} /> Official Election Schedule
                </div>
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: isNotStarted ? '#FF9900' : isEnded ? '#EF4444' : '#10B981',
                    backgroundColor: 'rgba(255, 255, 255, 0.06)',
                    padding: '3px 8px',
                    borderRadius: '6px',
                  }}
                >
                  {isNotStarted ? 'Pending Opening' : isEnded ? 'Ended' : 'Active'}
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  fontSize: '13px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '6px',
                  }}
                >
                  <span style={{ color: '#9CA3AF' }}>Scheduled Polls Opening:</span>
                  <strong style={{ color: '#FFFFFF', fontFamily: "'Noto Sans', sans-serif" }}>
                    {formatDateTime(election?.startTime) || 'Tue, Sep 22, 2026, 8:00 AM'}
                  </strong>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '6px',
                  }}
                >
                  <span style={{ color: '#9CA3AF' }}>Scheduled Polls Deadline:</span>
                  <strong style={{ color: '#FFFFFF', fontFamily: "'Noto Sans', sans-serif" }}>
                    {formatDateTime(election?.endTime) || 'Tue, Sep 22, 2026, 4:00 PM'}
                  </strong>
                </div>
              </div>
            </div>
          )}

          {/* Action CTAs & Homepage Links */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px' }}>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center', width: '100%' }}>
              <Link
                to="/"
                className="eac-submit"
                style={{
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '14px 28px',
                  minWidth: '220px',
                }}
              >
                <Home size={16} color="#FFFFFF" />
                <p style={{ margin: 0 }}>Return to Homepage</p>
              </Link>

              {(isEnded || isAlreadyVoted) && (
                <Link
                  to="/results"
                  style={{
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '14px 24px',
                    borderRadius: '8px',
                    backgroundColor: '#F3F4F6',
                    border: '1px solid #E5E7EB',
                    color: '#171619',
                    fontFamily: "'Syne', sans-serif",
                    fontWeight: 700,
                    fontSize: '15px',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <BarChart2 size={16} />
                  <span>View Live Results</span>
                </Link>
              )}
            </div>

            {/* Homepage Sync: Issues Logging in? Send us a message */}
            <div className="logincomplaints" style={{ marginTop: '12px' }}>
              <p style={{ margin: 0, fontSize: '13.5px', color: '#6B7280' }}>
                Issues or questions?{' '}
                <a
                  href="/#rapf"
                  style={{
                    color: '#FF9900',
                    textDecoration: 'underline',
                    fontWeight: 600,
                  }}
                >
                  Send us a message
                </a>
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Official Electpoll Footer */}
      <Footer />
    </div>
  );
};
